import { api } from "@/lib/api-client";
import type { CreateLineAtomicRequest, CreateLineAtomicResponse } from "@opus-dt/shared-types";
import type { LineDraftHeader, DraftLine } from "./useLineDraftState";
import type { LineSummary } from "@/features/lines/hooks";

export interface SaveLineAtomicResult {
  lineId: string;
  partialFailures: Array<{ step: string; message: string }>;
}

export interface SaveLineAtomicDeps {
  createLine: (data: {
    name: string;
    description?: string;
    responsible?: string;
    productionUnit: string;
  }) => Promise<LineSummary>;
}

export async function saveLineAtomic(
  header: LineDraftHeader,
  deps: SaveLineAtomicDeps
): Promise<SaveLineAtomicResult> {
  const payload = {
    name: header.name.trim(),
    description: header.description.trim() || undefined,
    responsible: header.responsible.trim() || undefined,
    productionUnit: header.productionUnit.trim(),
  };
  const created = await deps.createLine(payload);
  return { lineId: created.id, partialFailures: [] };
}

export interface SaveLineAtomicFullResult {
  lineId: string;
  idMap: CreateLineAtomicResponse["idMap"];
  line: CreateLineAtomicResponse["line"];
}

export interface SaveLineAtomicFullDeps {
  postAtomic?: (
    payload: CreateLineAtomicRequest,
    headers: { "Idempotency-Key": string }
  ) => Promise<CreateLineAtomicResponse>;
}

export function draftToAtomicPayload(draft: DraftLine): CreateLineAtomicRequest {
  return {
    header: {
      name: draft.header.name.trim(),
      description: draft.header.description.trim(),
      responsible: draft.header.responsible.trim(),
      productionUnit: draft.header.productionUnit.trim(),
    },
    machines: Object.values(draft.machines),
    canvas: draft.canvas,
  };
}

export async function saveLineAtomicFull(
  draft: DraftLine,
  idempotencyKey: string,
  deps: SaveLineAtomicFullDeps = {}
): Promise<SaveLineAtomicFullResult> {
  const payload = draftToAtomicPayload(draft);

  const post =
    deps.postAtomic ??
    (async (p, h) =>
      api.post<CreateLineAtomicResponse>("/api/v1/lines/atomic", p, {
        headers: h,
      }));

  const response = await post(payload, { "Idempotency-Key": idempotencyKey });
  return {
    lineId: response.lineId,
    idMap: response.idMap,
    line: response.line,
  };
}
